import { Component, inject, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';

import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { IonicModule, NavController } from '@ionic/angular/lazy';
import { BaseSetupPage } from '@app/core/base/base-setup-page';
import { UserFormBuilderService } from '@app/shared/services';
import { BaseButtonComponent } from '@app/shared/components/ui/base-button/base-button.component';
import { FormInputComponent } from '@app/shared/components/ui/form-input/form-input.component';
import { FormTextareaComponent } from '@app/shared/components/ui/form-textarea/form-textarea.component';
import {
  FormSelectComponent,
  SelectOption,
} from '@app/shared/components/ui/form-select/form-select.component';
import { takeUntil } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { API } from '@app/shared/constants/api-endpoints';
import {
  ProfileItem,
  ProfileItemsResponse,
} from '@app/shared/models/profile-edit.model';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-setup-profile',
  templateUrl: './setup-profile.page.html',
  styleUrls: ['./setup-profile.page.scss'],
  standalone: true,
  imports: [
    IonicModule,
    ReactiveFormsModule,
    BaseButtonComponent,
    FormInputComponent,
    FormTextareaComponent,
    FormSelectComponent,
  ],
})
export class SetupProfilePage extends BaseSetupPage implements OnInit {
  form: FormGroup<{
    username: FormControl<string>;
    displayName: FormControl<string>;
    bio: FormControl<string | null>;
  }>;
  /** 新規登録後の追加設定項目（API 取得） */
  additionalItems: { key: string; value: ProfileItem }[] = [];
  isLoadingAdditional = false;
  yearOptions: Array<{ label: string; value: string }> = [];
  monthOptions: Array<{ label: string; value: string }> = [];
  dayOptions: Array<{ label: string; value: string }> = [];
  /** テンプレート用。NG0100 回避のため getter ではなく次のティックで更新 */
  formValid = false;
  /** モーダルとして開かれた場合は true（ガードから開いた場合など） */
  @Input() isModal = false;
  protected userFormBuilder = inject(UserFormBuilderService);
  private readonly navController = inject(NavController);

  get yearOptionsWithPlaceholder() {
    return [{ label: '年', value: '' }, ...this.yearOptions];
  }

  get monthOptionsWithPlaceholder() {
    return [{ label: '月', value: '' }, ...this.monthOptions];
  }

  get dayOptionsWithPlaceholder() {
    return [{ label: '日', value: '' }, ...this.dayOptions];
  }

  isBirthdayItem(item: { key: string; value: ProfileItem }): boolean {
    return item.key === 'birthday';
  }

  isNicknameItem(item: { key: string; value: ProfileItem }): boolean {
    const normalizedKey = item.key.toLowerCase();
    const normalizedLabel = (item.value.label ?? '').toLowerCase();
    return (
      normalizedKey === 'name' ||
      normalizedKey === 'nickname' ||
      normalizedKey === 'nick_name' ||
      normalizedLabel.includes('ニックネーム')
    );
  }

  hasNicknameAdditionalItem(): boolean {
    return this.additionalItems.some((item) => this.isNicknameItem(item));
  }

  constructor() {
    super();
    this.form = this.userFormBuilder.userEditForm();
  }

  /**
   * このページで編集可能な項目だけを基準に保存可能かどうかを返す。
   * username は入力欄がないため含めない（API の user.name が英数字でない場合でも保存可能にする）。
   */
  private isSaveEnabled(): boolean {
    const form = this.form as FormGroup;
    if (!form.get('displayName')?.valid) return false;
    for (const item of this.additionalItems) {
      if (item.key === 'birthday') {
        if (
          !form.get('birthYear')?.valid ||
          !form.get('birthMonth')?.valid ||
          !form.get('birthDay')?.valid
        ) {
          return false;
        }
        continue;
      }
      if (!form.get(item.key)?.valid) return false;
    }
    return true;
  }

  /** フォーム有効状態を次のティックで更新（NG0100 回避） */
  private updateFormValid(): void {
    setTimeout(() => {
      this.formValid = this.isSaveEnabled();
      this.changeDetectorRef.markForCheck();
    }, 0);
  }

  ngOnInit() {
    this.ensureBirthdayControls();
    this.initBirthOptions();
    this.subscribeBirthdayChanges();

    this.form.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateFormValid());
    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateFormValid());
    this.updateFormValid();

    this.authStoreService.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (user) {
          // フォームの初期値を設定（profile がなくても name があればセット）
          this.form.patchValue({
            username: user.name || '',
            displayName: user.name || '',
            bio: null,
          });
          this.updateFormValid();
        }
      });

    // このページではユーザー名入力欄がないため、可用性チェックは行わない（ブロックされないようにする）
    this.fetchAdditionalRegisterItems();
  }

  /** 追加登録項目を取得（/api/v1/entry/additional_register_items） */
  async fetchAdditionalRegisterItems(): Promise<void> {
    this.isLoadingAdditional = true;
    try {
      const res = await firstValueFrom(
        this.apiService.post<ProfileItemsResponse>(
          API.ENTRY.ADDITIONAL_REGISTER_ITEMS,
          {}
        )
      );
      if (res?.result?.items && Object.keys(res.result.items).length > 0) {
        this.additionalItems = Object.entries(res.result.items).map(
          ([key, value]) => ({ key, value })
        );
        this.createAdditionalForm();
      }
    } catch (error) {
      console.error('Failed to fetch additional register items', error);
      this.toastService.showErrorToast('追加設定項目の取得に失敗しました');
    } finally {
      this.isLoadingAdditional = false;
      this.updateFormValid();
    }
  }

  private createAdditionalForm(): void {
    const form = this.form as FormGroup;
    for (const item of this.additionalItems) {
      const key = item.key;
      const config = item.value;
      // このページでは全項目必須（全部入力されるまで保存ボタンを押せない）
      if (key === 'birthday') {
        this.ensureBirthdayControls();
        continue;
      }

      if (config.type === 'checkbox-list') {
        form.addControl(
          key,
          new FormControl(
            [],
            [
              Validators.required,
              (c) =>
                Array.isArray(c.value) && c.value.length > 0
                  ? null
                  : { atLeastOne: true },
            ]
          )
        );
      } else {
        const initialValue: string | number | null =
          config.type === 'textarea' ? '' : null;
        form.addControl(
          key,
          new FormControl(initialValue, [Validators.required])
        );
      }
    }
  }

  private isOptionsListObject(
    list: string[] | Record<string, string> | undefined
  ): list is Record<string, string> {
    return (
      list != null &&
      typeof list === 'object' &&
      !Array.isArray(list) &&
      Object.keys(list).length > 0
    );
  }

  getOptions(item: ProfileItem): SelectOption[] {
    const list = item.options?.list;
    if (!list) return [];
    if (this.isOptionsListObject(list)) {
      return Object.entries(list).map(([value, label]) => ({ value, label }));
    }
    return (list as string[]).map((opt, i) => {
      return {
        label: opt,
        value: String(i),
      };
    });
  }

  getCheckboxOptions(item: ProfileItem): { value: string; label: string }[] {
    const list = item.options?.list;
    if (!list) return [];
    if (this.isOptionsListObject(list)) {
      return Object.entries(list).map(([value, label]) => ({ value, label }));
    }
    return (list as string[]).map((opt, i) => {
      return {
        value: String(i),
        label: opt,
      };
    });
  }

  getFieldError(key: string): string | null {
    const control = this.form.get(key);
    const err = control?.errors;
    if (err && typeof (err as any)['serverErrorMessage'] === 'string') {
      return (err as any)['serverErrorMessage'];
    }
    return null;
  }

  isCheckboxChecked(key: string, value: string): boolean {
    const control = this.form.get(key);
    if (!control || !control.value) return false;
    const currentValues = control.value;
    if (Array.isArray(currentValues)) return currentValues.includes(value);
    return currentValues === value;
  }

  onCheckboxChange(key: string, value: string, checked: boolean): void {
    const control = this.form.get(key);
    if (!control) return;
    let currentValues = control.value;
    if (!Array.isArray(currentValues)) {
      currentValues = currentValues ? [currentValues] : [];
    }
    if (checked) {
      if (!currentValues.includes(value)) {
        control.setValue([...currentValues, value]);
      }
    } else {
      control.setValue((currentValues as string[]).filter((v) => v !== value));
    }
  }

  private ensureBirthdayControls(): void {
    const form = this.form as FormGroup;
    if (!form.get('birthYear')) {
      form.addControl('birthYear', new FormControl('', [Validators.required]));
    }
    if (!form.get('birthMonth')) {
      form.addControl('birthMonth', new FormControl('', [Validators.required]));
    }
    if (!form.get('birthDay')) {
      form.addControl('birthDay', new FormControl('', [Validators.required]));
    }
  }

  private subscribeBirthdayChanges() {
    this.form
      .get('birthYear')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateDayOptions());
    this.form
      .get('birthMonth')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateDayOptions());
  }

  private initBirthOptions() {
    const currentYear = new Date().getFullYear();
    const startYear = 1950;
    this.yearOptions = Array.from({ length: currentYear - startYear + 1 })
      .map((_, i) => currentYear - i)
      .map((y) => ({ label: `${y}`, value: `${y}` }));

    this.monthOptions = Array.from({ length: 12 })
      .map((_, i) => i + 1)
      .map((m) => ({ label: `${m}`, value: `${m}` }));

    this.dayOptions = Array.from({ length: 31 })
      .map((_, i) => i + 1)
      .map((d) => ({ label: `${d}`, value: `${d}` }));
  }

  private updateDayOptions() {
    const year = Number(this.form.get('birthYear')?.value || 0);
    const month = Number(this.form.get('birthMonth')?.value || 0);
    const daysInMonth = this.getDaysInMonth(year, month) || 31;
    this.dayOptions = Array.from({ length: daysInMonth })
      .map((_, i) => i + 1)
      .map((d) => ({ label: `${d}`, value: `${d}` }));

    const currentDay = Number(this.form.get('birthDay')?.value || 0);
    if (currentDay > daysInMonth) {
      const birthDayControl = this.form.get('birthDay') as FormControl<
        string | null
      > | null;
      birthDayControl?.setValue('');
    }
  }

  private getDaysInMonth(year: number, month: number): number {
    if (!year || !month) return 31;
    return new Date(year, month, 0).getDate();
  }

  /** 追加項目の値を送信用オブジェクトに変換（form から additionalItems のキーのみ抽出） */
  getAdditionalFormPayload(): Record<string, string | string[]> {
    const body: Record<string, string | string[]> = {};
    for (const item of this.additionalItems) {
      const key = item.key;
      if (key === 'birthday') {
        const y = this.form.get('birthYear')?.value;
        const m = this.form.get('birthMonth')?.value;
        const d = this.form.get('birthDay')?.value;
        if (y && m && d) {
          body[key] = `${String(y)}-${String(m).padStart(2, '0')}-${String(
            d
          ).padStart(2, '0')}`;
        }
        continue;
      }
      const control = this.form.get(key);
      if (!control) continue;
      const v = control.value;
      if (Array.isArray(v)) {
        body[key] = v.map((item) => String(item));
      } else if (v != null && v !== '') {
        body[key] = String(v);
      }
    }
    return body;
  }

  /**
   * api/member/profile/update 用のリクエスト body を組み立てる。
   * name は displayName、その他は additional_register_items のキー・値。数値になり得る値は number で送る。
   */
  getProfileUpdateBody(): Record<string, string | number | string[]> {
    const additional = this.getAdditionalFormPayload();
    const body: Record<string, string | number | string[]> = {
      name: this.form.value.displayName ?? '',
    };
    for (const [key, value] of Object.entries(additional)) {
      if (Array.isArray(value)) {
        body[key] = value;
      } else if (value !== '' && /^\d+$/.test(value)) {
        body[key] = parseInt(value, 10);
      } else {
        body[key] = value;
      }
    }
    return body;
  }

  /**
   * フォームの送信処理
   */
  async onSubmit(): Promise<void> {
    if (!this.isSaveEnabled()) return;

    const updating = 'プロフィールを更新しています…';
    const updateSuccess = 'プロフィールを更新しました';
    const updateFailed = 'プロフィールの更新に失敗しました';

    const body = this.getProfileUpdateBody();

    try {
      await this.loadingService.showDuringTask(
        () => this.userService.updateMemberProfile(body),
        updating,
        'crescent'
      );
      await this.authService.refreshCurrentUser();
      await this.toastService.showSuccessToast(updateSuccess);
      if (this.isModal) {
        await this.modalController.dismiss({ completed: true });
      } else {
        await this.navController.navigateRoot('/tabs/home');
      }
    } catch (err: any) {
      const birthdayErrorMessage = this.extractBirthdayErrorMessage(err);
      if (birthdayErrorMessage) {
        await this.alertService.showAlert('入力エラー', birthdayErrorMessage);
        return;
      }

      const errorMessage = err.message ?? err?.error ?? updateFailed;
      await this.toastService.showErrorToast(errorMessage);
    }
  }

  private extractBirthdayErrorMessage(err: unknown): string | null {
    if (!err || typeof err !== 'object' || !('error' in err)) {
      return null;
    }

    const responseBody = (err as { error: unknown }).error;
    if (
      !responseBody ||
      typeof responseBody !== 'object' ||
      Array.isArray(responseBody)
    ) {
      return null;
    }

    const errorPayload = (responseBody as Record<string, unknown>)['error'];
    if (
      !errorPayload ||
      typeof errorPayload !== 'object' ||
      Array.isArray(errorPayload)
    ) {
      return null;
    }

    const birthdayErrors = (errorPayload as Record<string, unknown>)['birthday'];
    if (!Array.isArray(birthdayErrors) || birthdayErrors.length === 0) {
      return null;
    }

    const firstError = birthdayErrors[0];
    return typeof firstError === 'string' ? firstError : null;
  }

  /**
   * モーダル表示時に「あとでする」を押した場合。
   * 未完了のまま閉じ、ガード側で /tabs/home へ遷移する。
   */
  async onDismissLater(): Promise<void> {
    if (this.isModal) {
      await this.modalController.dismiss();
    }
  }
}
